import React, { useEffect, useState } from "react";
import styled from "styled-components";
import logo from "~/assets/i/logo.svg";
import { Transition, TransitionGroup } from "react-transition-group";
import { Box, Button, Icon, Stepper, Text } from "~/components-shared";
import Footer from "~/components/Footer";
import authActionCreators from "~/store/auth/actions";
import { useDispatch, useSelector } from "react-redux";
import SignUpOnboardingTeam from "~/components/SignUpOnboardingTeam";
import SignUpOnboardingTheme from "~/components/SignUpOnboardingTheme";
import SignUpOnboardingSelectIntegrations from "~/components/SignUpOnboardingSelectIntegrations";
import SignUpOnboardingIntegrate from "~/components/SignUpOnboardingIntegrate";
import { AuthoritiesEnum, MemberStatusEnum, StatusStateEnum } from "~/types/enums";
import companyActionCreators from "~/store/company/actions";
import companySelectors from "~/store/company/selectors";
import SignUpOnboardingSetTopics from "~/components/SignUpOnboardingSetTopics";
import authSelectors from "~/store/auth/selectors";
import lists from "~/lib/lists";
import { secured } from "react-abac";
import RestrictedSection from "~/components/RestrictedSection";
import { enterView, exitView } from "~/lib/animations";
const Section = styled.section`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 100vh;
  background-size: contain;
`;
const StyledButton = styled(Button)`
  position: absolute;
  top: 1em;
  right: 1em;
`;
const Branding = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${_ref => {
  let {
    theme
  } = _ref;
  return theme.spacing.step * 10;
}}px;
  margin-bottom: ${_ref2 => {
  let {
    theme
  } = _ref2;
  return theme.spacing.step * 10;
}}px;
`;
const Logo = styled.img`
  margin-right: ${_ref3 => {
  let {
    theme
  } = _ref3;
  return theme.spacing.step;
}}px;
`;
const SignUpOnboarding = () => {
  const steps = [{
    id: 1,
    status: MemberStatusEnum.SIGN_UP_TEAM,
    value: /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      weight: "semiBold"
    }, "Team")
  }, {
    id: 2,
    status: MemberStatusEnum.SIGN_UP_THEME,
    value: /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      weight: "semiBold"
    }, "Set Theme")
  }, {
    id: 3,
    status: MemberStatusEnum.SIGN_UP_INTEGRATIONS,
    value: /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      weight: "semiBold"
    }, "Select Integrations")
  }, {
    id: 4,
    status: MemberStatusEnum.SIGN_UP_INTEGRATE,
    value: /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      weight: "semiBold"
    }, "Integrate")
  }, {
    id: 5,
    status: MemberStatusEnum.SIGN_UP_TOPICS,
    value: /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      weight: "semiBold"
    }, "Identify Topics")
  }];
  const user = useSelector(authSelectors.getUser);
  const companyStatus = useSelector(companySelectors.getStatus);
  const dispatch = useDispatch();
  const memberSteps = steps.filter(step => user.authorities.includes(lists.members.statuses.toAuthorities[step.status]));
  const activeIndex = Object.values(memberSteps).findIndex(step => step.status === user.status);
  const [activeStep, setActiveStep] = useState(activeIndex + 1 || 0);
  const activeStatus = memberSteps[activeStep - 1].status;
  const handleLogOut = () => {
    dispatch(authActionCreators.loggedOut());
  };
  useEffect(() => {
    if (companyStatus === StatusStateEnum.IDLE) {
      dispatch(companyActionCreators.fetchStarted());
    }
  }, [companyStatus, dispatch]);
  const handleStepClick = id => {
    if (id <= activeIndex + 1) {
      setActiveStep(id);
    }
  };
  return /*#__PURE__*/React.createElement(Section, null, /*#__PURE__*/React.createElement(Branding, null, /*#__PURE__*/React.createElement(Logo, {
    src: logo,
    alt: "Joyn",
    width: 40
  }), /*#__PURE__*/React.createElement("h2", null, "Joyn")), /*#__PURE__*/React.createElement(Stepper, {
    maxWidth: "600px",
    activeValue: activeStep,
    data: memberSteps,
    s: {
      mb: 10
    },
    onChange: handleStepClick
  }), /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    justify: "start",
    align: "center",
    style: {
      flexGrow: 1
    }
  }, /*#__PURE__*/React.createElement(TransitionGroup, {
    component: null
  }, /*#__PURE__*/React.createElement(Transition, {
    key: activeStatus,
    onEnter: (node, appears) => enterView(node, appears),
    onExit: node => exitView(node),
    timeout: {
      enter: 750,
      exit: 400
    },
    appear: true
  }, /*#__PURE__*/React.createElement("div", null, activeStatus === MemberStatusEnum.SIGN_UP_TEAM && /*#__PURE__*/React.createElement(SignUpOnboardingTeam, {
    onContinue: () => setActiveStep(prev => prev + 1),
    s: {
      mb: 10
    }
  }), activeStatus === MemberStatusEnum.SIGN_UP_THEME && /*#__PURE__*/React.createElement(SignUpOnboardingTheme, {
    onContinue: () => setActiveStep(prev => prev + 1),
    s: {
      mb: 10
    }
  }), activeStatus === MemberStatusEnum.SIGN_UP_INTEGRATIONS && /*#__PURE__*/React.createElement(SignUpOnboardingSelectIntegrations, {
    onContinue: () => setActiveStep(prev => prev + 1),
    s: {
      mb: 10
    }
  }), activeStatus === MemberStatusEnum.SIGN_UP_INTEGRATE && /*#__PURE__*/React.createElement(SignUpOnboardingIntegrate, {
    onContinue: () => setActiveStep(prev => prev + 1),
    s: {
      mb: 10
    }
  }), activeStatus === MemberStatusEnum.SIGN_UP_TOPICS && /*#__PURE__*/React.createElement(SignUpOnboardingSetTopics, {
    onContinue: () => setActiveStep(prev => prev + 1),
    fullWidth: true,
    s: {
      mb: 10
    }
  }))))), /*#__PURE__*/React.createElement(StyledButton, {
    onClick: handleLogOut,
    color: "translucent",
    s: {
      px: 3,
      py: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "log-out",
    scale: 4,
    s: {
      mr: 2,
      mt: -0.5
    }
  }), "Log out"), /*#__PURE__*/React.createElement(Footer, null));
};
export default /*#__PURE__*/React.memo(secured({
  permissions: [AuthoritiesEnum.SIGN_UP_TEAM, AuthoritiesEnum.SIGN_UP_THEME, AuthoritiesEnum.SIGN_UP_INTEGRATIONS, AuthoritiesEnum.SIGN_UP_INTEGRATE, AuthoritiesEnum.SIGN_UP_TOPICS, AuthoritiesEnum.SIGN_UP_COMPLETE],
  noAccess: () => /*#__PURE__*/React.createElement(RestrictedSection, null)
})(SignUpOnboarding), () => true);